/**
 * Klasse DOKUMENTKONTEXT_JOB_COVERS_EXTRACT — Hintergrundanalyse chunkt den
 * vollen Extract-Cache. 7500 ist das LLM-Fenster pro Call, nicht das Mandat.
 */

export const PORTAL_ARKO_LLM_WINDOW_CHARS = 7500;
/** Hartes Max: kein Endlos-Loop. Rest ehrlich ungelesen. 102k-Extract ≈ 14 Fenster. */
export const PORTAL_ARKO_ANALYSIS_MAX_CHUNKS = 24;

/** needed = ceil(extractChars / windowChars) — aus der Länge, nicht aus Seiten. */
export function neededAnalysisWindows(
  extractChars: number,
  windowChars = PORTAL_ARKO_LLM_WINDOW_CHARS,
): number {
  const chars = Math.max(0, Math.floor(Number(extractChars)) || 0);
  const win = Math.max(1, Math.floor(Number(windowChars)) || PORTAL_ARKO_LLM_WINDOW_CHARS);
  if (chars <= 0) return 0;
  return Math.ceil(chars / win);
}

/** Darunter: Titel/Mini-Extract, nicht „passte in ein Fenster“ als Erfolg. */
export const PORTAL_ARKO_THIN_EXTRACT_CHARS = 800;
/** Kurze Notizen in den Folge-Call — kein Rohtext der vorigen Fenster. */
export const PORTAL_ARKO_ANALYSIS_NOTES_MAX_CHARS = 1600;

const PARAGRAPH_INDEX_RE = /\n=== Absatz-Index \(Portal Phase 1\.4\) ===[\s\S]*$/;

export type AnalysisStopReason = 'complete' | 'max_chunks' | 'timeout' | 'error' | 'cancelled';

export type AnalysisCoverage = {
  chunksDone: number;
  chunksPlanned: number;
  chunksCapped: number;
  remainderChars: number;
  extractChars: number;
  stopReason: AnalysisStopReason;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
  extractPages?: number;
};

export type AnalysisWindowPlan = {
  windows: string[];
  chunksPlanned: number;
  chunksCapped: number;
  remainderChars: number;
  extractChars: number;
  neededFromLength: number;
};

export type AnalysisJobPhase = 'section' | 'synthesis';

export type AnalysisProgress = {
  chunkIndex: number;
  chunkTotal: number;
  phase: AnalysisJobPhase;
  notes: string;
  charsRead?: number;
  charsTotal?: number;
};

export function stripParagraphIndexFromContext(documentContext: string): string {
  return documentContext.replace(PARAGRAPH_INDEX_RE, '').trimEnd();
}

export function extractDocumentContextBody(documentContext: string): string {
  const stripped = stripParagraphIndexFromContext(documentContext);
  const match = /Auszug \(UTF-8[^\n]*\):\n/u.exec(stripped);
  if (match && match.index != null) {
    return stripped.slice(match.index + match[0].length).trim();
  }
  return stripped.trim();
}

export function extractDocumentContextHeader(documentContext: string): string {
  const stripped = stripParagraphIndexFromContext(documentContext);
  const match = /Auszug \(UTF-8[^\n]*\):\n/u.exec(stripped);
  if (!match || match.index == null) {
    return stripped.split('\n').slice(0, 4).join('\n').trim();
  }
  return stripped.slice(0, match.index).trimEnd();
}

/** Ersetzt den Auszugs-Body; Absatz-Index entfällt (pro Fenster neu). */
export function replaceExtractBody(documentContext: string, newBody: string): string {
  const header = extractDocumentContextHeader(documentContext);
  const body = newBody.trim();
  const lines = header
    ? [header, '', 'Auszug (UTF-8, Extract-Cache):', body]
    : ['Auszug (UTF-8, Extract-Cache):', body];
  return lines.join('\n');
}

export function preferFullExtractForAnalysis(
  cappedContext: string,
  fullRaw?: string | null,
): string {
  const capped = stripParagraphIndexFromContext(cappedContext).trim();
  const full = (fullRaw || '').trim();
  if (!full) return capped;
  const cappedBody = extractDocumentContextBody(capped);
  if (full.length <= cappedBody.length + 80) return capped;
  return replaceExtractBody(capped, full);
}

function splitOnWhitespace(raw: string, limit: number): string[] {
  const out: string[] = [];
  let rest = raw;
  while (rest.length > limit) {
    const slice = rest.slice(0, limit);
    const lastWs = slice.search(/\s\S*$/);
    const cut = lastWs > limit * 0.55 ? slice.slice(0, lastWs) : slice;
    out.push(cut.trim());
    rest = rest.slice(cut.length).trimStart();
  }
  if (rest.trim()) out.push(rest.trim());
  return out.filter(Boolean);
}

export function splitExtractIntoWindows(
  body: string,
  windowChars = PORTAL_ARKO_LLM_WINDOW_CHARS,
): string[] {
  const text = body.replace(/\s+$/u, '').trim();
  if (!text) return [];
  if (text.length <= windowChars) return [text];
  // Länge zuerst: needed ≈ ceil(chars/window). Absatz-Packen erzeugte am 64-KB-Puffer 10 statt 9 Fenster.
  return splitOnWhitespace(text, windowChars);
}

export function planAnalysisWindows(
  fullDocumentContext: string,
  windowChars = PORTAL_ARKO_LLM_WINDOW_CHARS,
  maxChunks = PORTAL_ARKO_ANALYSIS_MAX_CHUNKS,
): AnalysisWindowPlan {
  const body = extractDocumentContextBody(fullDocumentContext);
  const all = splitExtractIntoWindows(body, windowChars);
  const neededFromLength = neededAnalysisWindows(body.length, windowChars);
  const chunksPlanned = Math.max(all.length, neededFromLength);
  const chunksCapped = Math.min(all.length, Math.max(1, maxChunks));
  const windows = all.slice(0, chunksCapped);
  const unread = all.slice(chunksCapped).join('\n\n');
  return {
    windows,
    chunksPlanned,
    chunksCapped,
    remainderChars: unread.length,
    extractChars: body.length,
    neededFromLength,
  };
}

export function trimAnalysisNotes(
  text: string,
  limit = PORTAL_ARKO_ANALYSIS_NOTES_MAX_CHARS,
): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= limit) return cleaned;
  const slice = cleaned.slice(0, limit);
  const lastWs = slice.search(/\s\S*$/);
  const cut = lastWs > limit * 0.7 ? slice.slice(0, lastWs) : slice;
  return `${cut.trimEnd()}…`;
}

export function joinPriorNotes(notes: string[]): string {
  if (notes.length === 0) return '';
  const labeled = notes.map((n, i) => `Abschnitt ${i + 1}: ${trimAnalysisNotes(n, 400)}`);
  return trimAnalysisNotes(labeled.join(' '), PORTAL_ARKO_ANALYSIS_NOTES_MAX_CHARS);
}

export function buildSectionWindowContext(args: {
  sourceContext: string;
  windowText: string;
  chunkIndex: number;
  chunkTotal: number;
  remainderChars: number;
  priorNotes: string;
}): string {
  const header = extractDocumentContextHeader(args.sourceContext);
  const index = buildWindowParagraphIndex(args.windowText);
  const remainderLine = args.remainderChars > 0
    || args.chunkIndex < args.chunkTotal
    ? `Extract-Rest nach diesem Job-Cap: ${args.remainderChars} Zeichen ungelesen, falls Abschnitte fehlen.`
    : 'Dieses Fenster ist das letzte des vorliegenden Extracts.';
  const notesBlock = args.priorNotes.trim()
    ? `\nBisherige Notizen (kein Rohtext):\n${args.priorNotes.trim()}\n`
    : '';
  return [
    header,
    '',
    `Analyse-Fenster ${args.chunkIndex}/${args.chunkTotal} (Deckel ${PORTAL_ARKO_LLM_WINDOW_CHARS} Zeichen pro Call).`,
    remainderLine,
    notesBlock,
    `Auszug (UTF-8, Analyse-Abschnitt ${args.chunkIndex}/${args.chunkTotal}):`,
    args.windowText.trim(),
    '',
    '=== Absatz-Index (dieser Abschnitt) ===',
    ...index,
  ].filter((line, i, arr) => !(line === '' && arr[i - 1] === '')).join('\n');
}

export function buildSynthesisContext(args: {
  sourceContext: string;
  notes: string[];
  coverage: AnalysisCoverage;
}): string {
  const header = extractDocumentContextHeader(args.sourceContext);
  const labeled = args.notes.map((n, i) => `${i + 1}. ${n.trim()}`).filter((n) => n.length > 3);
  return [
    header,
    '',
    `Synthese über gelesene Abschnitte ${args.coverage.chunksDone}/${args.coverage.chunksPlanned}.`,
    backgroundAnalysisCoverageHint(args.coverage),
    '',
    'Bisherige Abschnittsnotizen (kein Chunk-Rohtext):',
    labeled.join('\n') || '(keine Notizen)',
  ].join('\n');
}

function buildWindowParagraphIndex(windowText: string): string[] {
  const parts = windowText.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  const units = parts.length > 1 ? parts : windowText.split(/\n/).map((l) => l.trim()).filter(Boolean);
  return units.slice(0, 20).map((text, i) => {
    const snippet = text.length > 140 ? `${text.slice(0, 137)}…` : text;
    return `${i + 1}. [win-${i + 1}] ${snippet}`;
  });
}

export function backgroundAnalysisCoverageHint(coverage: AnalysisCoverage): string {
  const {
    chunksDone,
    chunksPlanned,
    remainderChars,
    stopReason,
    extractChars,
  } = coverage;
  const pagesBit = coverage.extractPages && coverage.extractPages > 0
    ? ` / ${coverage.extractPages} Seiten`
    : '';
  const raw = coverage.extractRawChars ?? extractChars;
  if (extractChars > 0 && extractChars < PORTAL_ARKO_THIN_EXTRACT_CHARS && chunksPlanned <= 1) {
    const src = coverage.extractSource?.trim();
    const srcBit = src ? ` (Quelle ${src})` : '';
    return `Extract nur ${extractChars} Zeichen${pagesBit}${srcBit} — Rest nicht im Textlayer. Kein Vollgutachten.`;
  }
  const charsBit = ` ${extractChars} von ${raw} Zeichen`;
  if (coverage.extractTruncated && raw > extractChars) {
    const rest = ` Extract-Puffer${charsBit}.`;
    if (stopReason === 'complete' && remainderChars === 0 && chunksDone >= chunksPlanned) {
      return `Gelesen: ${chunksDone}/${chunksPlanned} Abschnitte des Extract-Puffers.${rest}`;
    }
  }
  if (chunksPlanned <= 1 && remainderChars === 0 && stopReason === 'complete') {
    return `Gelesen: 1/${chunksPlanned} Abschnitte · Extract${charsBit} (ein Fenster, Deckel ${PORTAL_ARKO_LLM_WINDOW_CHARS} pro Call).`;
  }
  if (stopReason === 'complete' && remainderChars === 0 && chunksDone >= chunksPlanned) {
    return `Gelesen: ${chunksDone}/${chunksPlanned} Abschnitte ·${charsBit}.`;
  }
  const rest = remainderChars > 0
    ? ` Rest des Extracts (~${remainderChars} Zeichen) nicht gelesen.`
    : '';
  const capBit = stopReason === 'max_chunks'
    ? ` (Job-Cap ${coverage.chunksCapped})`
    : '';
  if (stopReason === 'timeout') {
    return `Gelesen: Abschnitte 1–${chunksDone} von ${chunksPlanned} — Zeitlimit.${rest}`;
  }
  if (stopReason === 'max_chunks') {
    return `Gelesen: Abschnitte 1–${chunksDone} von ${chunksPlanned}${capBit}.${rest}`;
  }
  if (stopReason === 'error') {
    return `Gelesen: Abschnitte 1–${chunksDone} von ${chunksPlanned} — weiterer Abschnitt fehlgeschlagen.${rest}`;
  }
  return `Gelesen: Abschnitte 1–${chunksDone} von ${chunksPlanned}.${rest}`;
}

export function backgroundAnalysisProgressHint(progress: AnalysisProgress): string {
  const chars = progress.charsTotal && progress.charsTotal > 0
    ? ` — ${progress.charsRead ?? 0} / ${progress.charsTotal} Zeichen`
    : '';
  if (progress.phase === 'synthesis') {
    return `Synthese der gelesenen Abschnitte 1–${progress.chunkIndex} von ${progress.chunkTotal}${chars}.`;
  }
  return `Abschnitt ${progress.chunkIndex}/${progress.chunkTotal}${chars} — je Call max. ${PORTAL_ARKO_LLM_WINDOW_CHARS} Zeichen. Kein Vollgutachten aus einem Fenster.`;
}

export function composeBackgroundAnalysisWorkerText(args: {
  synthesisReply: string;
  sectionNotes: string[];
  coverage: AnalysisCoverage;
}): string {
  const notes = args.sectionNotes
    .map((n, i) => `— Abschnitt ${i + 1}: ${n.trim()}`)
    .filter((n) => n.length > 16)
    .join('\n');
  return [
    backgroundAnalysisCoverageHint(args.coverage),
    '',
    args.synthesisReply.trim() || 'Keine Synthese geliefert.',
    notes ? `\n--- Abschnittsnotizen ---\n${notes}` : '',
  ].filter(Boolean).join('\n').trim();
}

export function coverageFromPlan(
  plan: AnalysisWindowPlan,
  chunksDone: number,
  stopReason: AnalysisStopReason,
  extra?: Pick<AnalysisCoverage, 'extractRawChars' | 'extractTruncated' | 'extractSource' | 'extractPages'>,
): AnalysisCoverage {
  return {
    chunksDone,
    chunksPlanned: plan.chunksPlanned,
    chunksCapped: plan.chunksCapped,
    remainderChars: plan.remainderChars,
    extractChars: plan.extractChars,
    stopReason,
    ...extra,
  };
}
